/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  RevenueByMonthInitialProperties,
  RevenueByMonthResponseInterface,
  RequestRevenueByMonthInterFace,
} from '../../interface/by-month-revenue.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchRevenueByMonth = createAsyncThunk<
  RevenueByMonthResponseInterface,
  RequestRevenueByMonthInterFace
>('by-month/fetchRevenueByMonth', async (data: RequestRevenueByMonthInterFace, { dispatch }) => {
  const { accountCustomId, ...query } = data;
  if (!accountCustomId) {
    throw new Error('accountCustomId is required');
  }
  try {
    const response = await accountApi.fetchRevenueByMonthList(
      accountCustomId,
      removeEmptyKeys(query),
    );
    return response.data;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code  || 'Something went wrong!',
        status: true,
      }),
    );
    throw error;
  }
});

export const fetchRevenueByMonthFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'by-month/fetchRevenueByMonthFilters',
  async (_, { dispatch }) => {
    try {
      const response = await accountApi.fetchAccountsListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: RevenueByMonthInitialProperties = {
  RevenueByMonth: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const RevenueByMonthSlice = createSlice({
  name: 'by-month-account',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateByMonthFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableByMonthFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Clear all Data and reset related state
     */
    clearByMonthData: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RevenueByMonthInitialProperties>) => {
    builder
      .addCase(fetchRevenueByMonth.pending, (state: RevenueByMonthInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRevenueByMonth.fulfilled,
        (
          state: RevenueByMonthInitialProperties,
          action: PayloadAction<RevenueByMonthResponseInterface>,
        ) => {
          state.loading = false;
          state.RevenueByMonth = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRevenueByMonth.rejected, (state: RevenueByMonthInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchRevenueByMonthFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRevenueByMonthFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchRevenueByMonthFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateByMonthFilters, disableByMonthFilterApplied, setSortData ,clearByMonthData} =
  RevenueByMonthSlice.actions;
// Export the reducer
export default RevenueByMonthSlice.reducer;
